import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { GetShiftPlanOutputResponse } from "../../../reviewAndPublish/model";
import { buildProcessPathData } from "./processPathRows";

/**
 * Client-side PDF export for the published shift plan (UTR-REFLEX-1163).
 *
 * Builds a structured, text-based PDF from the fetched getShiftPlanOutput data
 * (jsPDF + jspdf-autotable) — NOT a screenshot, so the values are selectable and
 * the recharts SVG chart is represented as a data table rather than a raster image.
 */

export interface PlanPdfMetadata {
  /** Human-readable plan label, for display only (e.g. "Operator Override Plan"). */
  planType: string;
  /** Stable flag driving label logic — set from the plan-view identity, not the
   *  display string, so wording changes to planType don't affect the PDF content. */
  isRccProposedView: boolean;
  version: string;
  date: string;
  shift: string;
  stationCode: string;
}

const numberFormatter = new Intl.NumberFormat("en-US");

const formatNumber = (value?: number | null): string =>
  value === undefined || value === null ? "--" : numberFormatter.format(value);

// Filename: shift-plan_<FC>_<date>_<shift>_<planType>.pdf, spaces/slashes stripped.
export const buildPlanPdfFilename = (meta: PlanPdfMetadata): string => {
  const slug = (value: string): string =>
    (value || "unknown").trim().replace(/[^a-zA-Z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  return `shift-plan_${slug(meta.stationCode)}_${slug(meta.date)}_${slug(meta.shift)}_${slug(meta.planType)}.pdf`;
};

/**
 * Build the shift-plan PDF document from the plan output + metadata and return the
 * jsPDF instance (kept separate from the download side-effect so it's unit-testable).
 */
export const buildPlanPdf = (
  shiftPlanOutput: GetShiftPlanOutputResponse,
  meta: PlanPdfMetadata
): jsPDF => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  let cursorY = 48;

  // Title.
  doc.setFontSize(16);
  doc.text("RCC Published Shift Plan", marginX, cursorY);
  cursorY += 22;

  // Metadata line.
  doc.setFontSize(10);
  doc.text(
    `${meta.planType}  •  ${meta.stationCode}  •  ${meta.date}  •  ${meta.shift}  •  Version: ${meta.version}`,
    marginX,
    cursorY
  );
  cursorY += 20;

  const totals = shiftPlanOutput.shiftTotals;
  const primary = shiftPlanOutput.primaryGradingVolume;
  const headColor: [number, number, number] = [33, 98, 161];

  // Recommended Volume: the total units across the shift = the peak of the
  // cumulative projected curve, matching the on-screen metric card
  // (TopMetricsRow uses Math.max of the curve, falling back to recommended.total).
  const curvePeak = (shiftPlanOutput.projectedPrimaryGradingVolume ?? [])
    .map((point) => point.recommended)
    .filter((value): value is number => value != null);
  const recommendedVolume = curvePeak.length > 0
    ? Math.max(...curvePeak)
    : primary?.recommended?.total;

  // Section 1 — Summary metrics (RCC Recommended, ALPS Planned, Labor Hours Delta).
  autoTable(doc, {
    startY: cursorY,
    head: [["Summary", "Value"]],
    body: [
      ["RCC Recommended Volume", `${formatNumber(recommendedVolume)} units`],
      ["ALPS Planned Volume", `${formatNumber(primary?.recommended?.alps)} units`],
      ["Labor Hours Delta", `${formatNumber(totals?.showHoursNeeded?.intValue)} hrs`],
      [
        "Labor Hours Delta %",
        // == null guards both null and undefined, matching formatNumber's
        // treatment of missing values (else Math.abs(null) would render "0.0%").
        totals?.laborHoursDeltaPercent == null
          ? "--"
          : `${Math.abs(totals.laborHoursDeltaPercent).toFixed(1)}%`,
      ],
    ],
    theme: "striped",
    headStyles: { fillColor: headColor },
    margin: { left: marginX, right: marginX },
  });

  // Section 2 — Shift Totals (hours breakdown).
  autoTable(doc, {
    head: [["Shift Totals", "Hours"]],
    body: [
      ["Regular Scheduled Hours", formatNumber(totals?.regularScheduledHours?.intValue)],
      ["VET Accepted", formatNumber(totals?.vetAcceptedHours?.intValue)],
      ["Ready Accepted", formatNumber(totals?.amazonReadyAcceptedHours?.intValue)],
      ["VTO Accepted", formatNumber(totals?.vtoAcceptedHours?.intValue)],
      ["Total Available Hours", formatNumber(totals?.totalAvailableHours?.intValue)],
    ],
    theme: "striped",
    headStyles: { fillColor: headColor },
    margin: { left: marginX, right: marginX },
  });

  // Section 3 — projected volume curve (the chart's underlying data). The SVG
  // chart can't be embedded with jsPDF, so the hourly points are tabulated. The
  // curve belongs to whichever plan is being downloaded, so the heading follows
  // the plan view (Operator Override plan → its own volume, not RCC recommended).
  const volumeHeading = meta.isRccProposedView ? "RCC Recommended Volume" : "Operator Override Volume";
  const projected = shiftPlanOutput.projectedPrimaryGradingVolume ?? [];
  if (projected.length > 0) {
    autoTable(doc, {
      head: [[volumeHeading, "Units"]],
      body: projected.map((point) => [point.time, formatNumber(point.recommended)]),
      theme: "striped",
      headStyles: { fillColor: headColor },
      margin: { left: marginX, right: marginX },
    });
  }

  // Section 4 — Process Path Analysis: the full Headcount and Hours tables — every
  // process-path row per time slot plus a Total rollup, matching the on-screen table.
  const summary = (shiftPlanOutput.shiftSummary ?? []).filter(
    (category) => category.planOutput && category.planOutput.length > 0
  );
  summary.forEach((category) => {
      // Shared derivation (labels, per-slot values with 0-fill, descending sort by
      // first slot, column totals) — kept identical to the on-screen table.
      const { timeSlots: slots, rows: dataRows, totals } = buildProcessPathData(category);

      // One row per label; value per slot, formatted.
      const rows = dataRows.map((row) => [
        row.label,
        ...row.values.map((value) => formatNumber(value)),
      ]);

      const totalRow = ["Total", ...totals.map((value) => formatNumber(value))];

      const title = category.heading === "headcount"
        ? "Headcount"
        : category.heading === "hours"
          ? "Hours"
          : `${category.heading.charAt(0).toUpperCase()}${category.heading.slice(1)}`;

      // Each process-path table (Headcount, Hours) starts on its own page.
      doc.addPage();
      doc.setFontSize(14);
      doc.text(`${title} Totals`, marginX, 48);

      autoTable(doc, {
        startY: 64,
        head: [[title, ...slots]],
        body: [...rows, totalRow],
        theme: "grid",
        headStyles: { fillColor: headColor },
        // Bold the trailing Total row.
        didParseCell: (data: any) => {
          if (data.section === "body" && data.row.index === rows.length) {
            data.cell.styles.fontStyle = "bold";
          }
        },
        styles: { fontSize: 7 },
        margin: { left: marginX, right: marginX },
      });
    });

  return doc;
};

/**
 * Build and trigger a browser download of the shift-plan PDF.
 */
export const downloadPlanPdf = (
  shiftPlanOutput: GetShiftPlanOutputResponse,
  meta: PlanPdfMetadata
): void => {
  const doc = buildPlanPdf(shiftPlanOutput, meta);
  doc.save(buildPlanPdfFilename(meta));
};
